import { v4 as uuidv4 } from "uuid";
import {
  CONFIDENT_MATCH_DELTA_MINUTES,
  toIsoDate,
} from "@/composables/recordbooks/useRecordBookMapper";

// Clés d'un cahier d'acquisition jamais lues depuis une ligne d'essai et trop
// lourdes pour y être recopiées : elles partiraient dans la payload de
// génération et dans les brouillons. Ajouter ici toute clé à ne plus embarquer.
export const ACQUISITION_BOOK_OMITTED_KEYS = ["graphsPoint"];

// Copie superficielle d'un cahier d'acquisition allégée pour une ligne d'essai.
export const toRowAcquisitionBook = (book) => {
  if (!book) return null;
  const rowBook = { ...book };
  for (const key of ACQUISITION_BOOK_OMITTED_KEYS) delete rowBook[key];
  return rowBook;
};

// Deux essais d'une même phase partagent l'uuid du sample : seul rowId
// identifie une ligne du tableau des essais (suppression/duplication/édition).

export const withRowIds = (rows) =>
  rows.map((row) => (row?.rowId ? row : { ...row, rowId: uuidv4() }));

// fallback par référence : une ligne pas encore normalisée (rowId absent)
// ne doit jamais matcher les autres lignes sans rowId
export const removeRow = (rows, row) =>
  row.rowId
    ? rows.filter(({ rowId }) => rowId !== row.rowId)
    : rows.filter((candidate) => candidate !== row);

export const findRowIndex = (rows, row) =>
  row.rowId
    ? rows.findIndex(({ rowId }) => rowId === row.rowId)
    : rows.indexOf(row);

export const filterRowsByTargets = (rows, targets) => {
  const targetIds = new Set(
    targets.map((target) => target?.rowId).filter(Boolean)
  );
  return rows.filter(({ rowId }) => targetIds.has(rowId));
};

const hasHumidityBook = (row) =>
  !!(row.humidityUuid || row.humidityRecordBook?.uuid);

const hasSpeedBookWithoutTrial = (row) =>
  !!(row.speedUuid || row.speedRecordBook?.uuid) && row.speedIndex == null;

const hasNoEndTime = (row) => !!row.startTime && !row.endTime;

const isDuplicateSample = (row, rows) =>
  (row.isManualSample || row.isSum) &&
  !!row.uuid &&
  rows.filter(({ uuid }) => uuid === row.uuid).length > 1;

const isMatchUncertain = (row) =>
  [row.humidityRecordBook?.matchDeltaMinutes, row.speedMatchDeltaMinutes].some(
    (delta) => Number.isFinite(delta) && delta > CONFIDENT_MATCH_DELTA_MINUTES
  );

const majorityDay = (rows) => {
  const countByDay = {};
  rows.forEach((row) => {
    const day = toIsoDate(row.date);
    if (day) countByDay[day] = (countByDay[day] || 0) + 1;
  });
  const days = Object.keys(countByDay);
  if (days.length < 2) return null;
  const topCount = Math.max(...Object.values(countByDay));
  const topDays = days.filter((day) => countByDay[day] === topCount);
  return topDays.length === 1 ? topDays[0] : null;
};

const sameDayRows = (row, rows) => {
  const day = toIsoDate(row.date);
  return rows.filter((candidate) => toIsoDate(candidate.date) === day);
};

// allowSeveralDays : une campagne QAL2/AST s'étale sur plusieurs jours, chacun
// avec ses propres cahiers ; les cahiers minoritaires se comparent au sein du jour.
export const getRowWarning = (row, rows, { allowSeveralDays = false } = {}) => {
  if (!hasHumidityBook(row)) return "missing-humidity";
  if (hasSpeedBookWithoutTrial(row)) return "missing-speed-trial";
  if (isDuplicateSample(row, rows)) return "duplicate-sample";
  if (hasNoEndTime(row)) return "missing-end-time";

  const reference = allowSeveralDays ? null : majorityDay(rows);
  if (reference && toIsoDate(row.date) !== reference) return "other-day";

  const peers = allowSeveralDays ? sameDayRows(row, rows) : rows;
  const minorityBook = (key) => {
    const countByBook = {};
    peers.forEach((candidate) => {
      const uuid = candidate[key];
      if (uuid) countByBook[uuid] = (countByBook[uuid] || 0) + 1;
    });
    const uuids = Object.keys(countByBook);
    if (uuids.length < 2 || !row[key]) return false;
    return countByBook[row[key]] < Math.max(...Object.values(countByBook));
  };
  if (minorityBook("speedUuid")) return "other-speed-book";
  if (minorityBook("manualUuid")) return "other-sampling-book";
  if (isMatchUncertain(row)) return "uncertain-match";

  return null;
};

export const getRowWarnings = (rows, options = {}) => [
  ...new Set(
    rows.map((row) => getRowWarning(row, rows, options)).filter(Boolean)
  ),
];

export const ROW_MOTIFS_BY_ISSUE = {
  "multiple-days": ["other-day"],
  "missing-record-books": ["missing-humidity", "missing-speed-trial"],
  "row-duplicate-sample": ["duplicate-sample"],
  "row-missing-speed-trial": ["missing-speed-trial"],
  "row-uncertain-match": ["uncertain-match"],
  "row-other-speed-book": ["other-speed-book"],
  "row-other-sampling-book": ["other-sampling-book"],
};

export const getRowClass = (row, rows, options = {}) => {
  const warning = getRowWarning(row, rows, options);
  const highlightedMotifs = options.highlightedMotifs || [];
  const classes = warning ? ["sample-row--warning"] : [];
  if (!highlightedMotifs.length) return classes.join(" ");

  const hasTarget = rows.some((candidate) =>
    highlightedMotifs.includes(getRowWarning(candidate, rows, options))
  );
  if (!hasTarget) return classes.join(" ");

  const isTarget = highlightedMotifs.includes(warning);
  if (options.releasing)
    classes.push(isTarget ? "sample-row--emphasis-out" : "sample-row--dim-out");
  else classes.push(isTarget ? "sample-row--highlight" : "sample-row--dimmed");

  return classes.join(" ");
};
